// src/pages/quality/AllAssessmentsPage.jsx
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import AssessmentTypeTable from '../../components/quality/Analytics/AssessmentTypeTable';
import ReceiptChannelTable from '../../components/quality/Analytics/ReceiptChannelTable';
import ProjectCategoryTable from '../../components/quality/Analytics/ProjectCategoryTable';
import OperatorsTable from '../../components/quality/Analytics/OperatorsTable';
import ImpactTable from '../../components/quality/Analytics/ImpactTable';
import OperatorCriteriaTable from '../../components/quality/Analytics/OperatorCriteriaTable';
import ImpactOperatorTable from '../../components/quality/Analytics/ImpactOperatorTable';
import OperatorServiceTable from '../../components/quality/Analytics/OperatorServiceTable';

import {
  useDateTimeFilter,
  toBackendFormat,
  toInputFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from '../../hooks/useDateTimeFilter';

const AllAssessmentsPage = () => {
  const { projects } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImpact, setSelectedImpact] = useState('');
  const [months, setMonths] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState('')
  const [ticketsCount, setTicketsCount] = useState([]);
  const [missingDates, setMissingDates] = useState([]);
  const [isDateManuallyChanged, setIsDateManuallyChanged] = useState(false);
  const [isSyncingFilters, setIsSyncingFilters] = useState(false); // Флаг для предотвращения повторного запроса при синхронизации
  const [isFetching, setIsFetching] = useState(false); // Флаг для блокировки повторных запросов

  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const projectId = pathParts[pathParts.length - 1];
  const project = projects.find(p => p.id === parseInt(projectId));

  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;
  useEffect(() => {
    // Находим проект по ID из последнего сегмента URL
    const project = projects.find(p => p.id === parseInt(projectId));
    setSelectedProject(project);
  }, [projects, projectId]);
  console.log('ticketsCount: ', ticketsCount)
  
  // Отслеживаем ручное изменение дат
  useEffect(() => {
    if (tempDateValues.date_filter_from !== dateFilters.date_filter_from ||
        tempDateValues.date_filter_to !== dateFilters.date_filter_to) {
      setIsDateManuallyChanged(true);
    }
  }, [dateFilters.date_filter_from, dateFilters.date_filter_to, tempDateValues]);

  // Сброс месяца при ручном вводе дат
  useEffect(() => {
    if (isDateManuallyChanged && (dateFilters.date_filter_from || dateFilters.date_filter_to) && selectedMonthYear) {
      setSelectedMonthYear('');
    }
  }, [isDateManuallyChanged, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);
  useEffect(() => {
    // Прерываем выполнение, если идет синхронизация фильтров или уже выполняется запрос
    if (isSyncingFilters || isFetching) {
      console.log('Пропуск загрузки данных: идет синхронизация фильтров или уже выполняется запрос');
      return;
    }
    
    const fetchAllData = async () => {
      // Повторная проверка внутри, на всякий случай
      if (isFetching) return;
      
      if (!selectedProject) return;

      try {
        setIsFetching(true);
        setLoading(true);

        const dateFrom = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
        const dateTo = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

        // Форматируем даты для фильтрации тикетов, проверяя их валидность
        const dateFromFormatted = dateFilters.date_filter_from && dayjs(dateFilters.date_filter_from).isValid() ? dayjs(dateFilters.date_filter_from).format('YYYY-MM-DD') : '';
        const dateToFormatted = dateFilters.date_filter_to && dayjs(dateFilters.date_filter_to).isValid() ? dayjs(dateFilters.date_filter_to).format('YYYY-MM-DD') : '';
        
        // Удаляем предыдущую переменную allDatesLoaded
        // let allDatesLoaded = true;
        
        // Извлекаем month и year из selectedMonthYear
        const monthYearParts = selectedMonthYear ? selectedMonthYear.split('-') : [];
        const month = monthYearParts[0] || '';
        const year = monthYearParts[1] || '';
        
        // Загружаем оценки для выбранного проекта
        const assessmentsResponse = await api.get(`/quality/assessments`, {
          params: {
            project_id: selectedProject.id,
            date_filter_from: dateFrom,
            date_filter_to: dateTo,
            month: month,
            year: year
          }
        });
        setAssessments(assessmentsResponse.data.data || []);

        // Загружаем все опции полей для проекта
        const optionsResponse = await api.get(`/field-options/all?project_id=${selectedProject.id}`);
        // Загружаем операторов
        const operatorsResponse = await api.get(`/operators?project_id=${selectedProject.id}`);
        let operatorsList = operatorsResponse.data.operators || [];
        
        // Проверяем, есть ли в assessments agent_name, которых нет в operatorsList
        const uniqueAgentNames = [...new Set(assessments.map(a => a.agent_name).filter(Boolean))];
        
        uniqueAgentNames.forEach(agentName => {
          // Проверяем, есть ли уже такой оператор в списке
          const exists = operatorsList.some(op => 
            `${op.surname} ${op.name}` === agentName
          );
          
          if (!exists) {
            // Разделяем имя на фамилию и имя
            const nameParts = agentName.trim().split(' ');
            if (nameParts.length >= 2) {
              const surname = nameParts[0];
              const name = nameParts[1];
              
              operatorsList.push({
                id: -1, // временный ID для отсутствующих в базе операторов
                surname: surname,
                name: name
              });
            }
          }
        });
        
        if (optionsResponse.data.success) {
          setFieldOptions(prev => ({
            ...optionsResponse.data.data,
            operators: operatorsList
          }));
        }

        // Загружаем все критерии проекта
        const criteriaResponse = await api.get(`/quality/criteria?project_id=${selectedProject.id}`);
        if (criteriaResponse.data.success) {
          setCriteria(criteriaResponse.data.data || []);
        }
        
        // Загружаем месяцы для выпадающего списка
        const monthsResponse = await api.get(`/quality/assessment-months`, {
          params: { project_id: selectedProject.id }
        });
        
        // Загружаем количество тикетов
        const ticketsResponse = await api.get(`/tickets-count-by-day`, {
          params: {
            project_id: selectedProject.id,
            date_from: dateFromFormatted,
            date_to: dateToFormatted,
            month: month,
            year: year
          }
        });
        
        setTicketsCount(ticketsResponse.data.records || []);
        
        // После загрузки данных, определяем отсутствующие даты
        if (dateFilters.date_filter_from && dateFilters.date_filter_to && ticketsResponse.data.records.length > 0) {  // Проверяем исходные dateFrom и dateTo
          let start = dayjs(dateFilters.date_filter_from);
          let end = dayjs(dateFilters.date_filter_to);
          
          // Проверяем, что start и end являются валидными dayjs объектами
          if (!start.isValid()) {
            start = dayjs(); // Используем текущую дату как fallback
          }
          if (!end.isValid()) {
            end = dayjs(); // Используем текущую дату как fallback
          }
          
          // Убедимся, что start не позже end
          if (start.isAfter(end)) {
            [start, end] = [end, start]; // Меняем местами
          }
          
          // Создаем массив всех дат в диапазоне без использования isSameOrBefore
          const allDatesInRange = [];
          let currentDate = start.clone();
          const endDate = end.endOf('day'); // Устанавливаем время конца дня
          
          // Получаем timestamp для сравнения
          const endTimestamp = endDate.valueOf();
          
          while (currentDate.valueOf() <= endTimestamp) {
            allDatesInRange.push(currentDate.format('YYYY-MM-DD'));
            currentDate = currentDate.add(1, 'day');
          }
          
          // Получаем даты, которые есть в ответе от API
          const existingDates = ticketsResponse.data.records.map(record => record.count_date);
          
          // Находим отсутствующие даты
          const missing = allDatesInRange.filter(date => !existingDates.includes(date));
          setMissingDates(missing);
        } else {
          // Если фильтры дат не заданы, задана только одна дата, или нет записей,
          // нет информации об отсутствующих датах
          setMissingDates([]);
        }
        
        if (monthsResponse.data.success) {
          setMonths(monthsResponse.data.months);
        }
        
        if (assessmentsResponse.data && assessmentsResponse.data.filters) {
          // Устанавливаем флаг синхронизации ВСЕХ фильтров
          setIsSyncingFilters(true);
          
          // Синхронизация месяца с сервера
          if (assessmentsResponse.data.filters.month && assessmentsResponse.data.filters.year) {
            const monthYearValue = `${assessmentsResponse.data.filters.month}-${assessmentsResponse.data.filters.year}`;
            if (monthYearValue !== selectedMonthYear) {
              setSelectedMonthYear(monthYearValue);
            }
          }

          // Синхронизация дат с сервера
          const serverFrom = assessmentsResponse.data.filters.date_filter_from || getDefaultDateFrom();
          const serverTo = assessmentsResponse.data.filters.date_filter_to || getDefaultDateTo();
          const inputFrom = toInputFormat(serverFrom);
          const inputTo = toInputFormat(serverTo);

          if (inputFrom !== dateFilters.date_filter_from) {
            handleDateBlur({target: {name: 'date_filter_from', value: inputFrom}});
          }
          if (inputTo !== dateFilters.date_filter_to) {
            handleDateBlur({target: {name: 'date_filter_to', value: inputTo}});
          }
          
          // Сбрасываем фильтр по месяцу, если используется фильтр по датам
          if (assessmentsResponse.data.filters.date_filter_from && assessmentsResponse.data.filters.date_filter_to) {
            setSelectedMonthYear('');
          }
        }
      } catch (err) {
        console.error('Ошибка загрузки данных:', err);
        setError('Не удалось загрузить данные');
      } finally {
        setLoading(false);
        setIsFetching(false);
        // Сбрасываем флаг после обновления ВСЕХ фильтров
        setTimeout(() => setIsSyncingFilters(false), 0);
      }
    };

    fetchAllData();
  }, [selectedProject, dateFilters.date_filter_from, dateFilters.date_filter_to, selectedMonthYear]);

  // Функция для получения названия месяца по номеру
  const getMonthName = (monthNumber) => {
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];
    return months[monthNumber - 1] || '';
  };

  // Формируем опции для выпадающего списка
  const monthYearOptions = months.map(m => ({
    value: `${m.month}-${m.year}`,
    label: `${getMonthName(m.month)} ${m.year}`
  }));

  console.log(assessments.length)

  return (
    <div className="main-content">
      <h1>Все оценки</h1>

      {loading ? (
        <div>Загрузка...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
          <div>
            <p>Проект: {selectedProject ? selectedProject.name_text : 'Не выбран'}</p>
            <p>Загружено проверок: {assessments.length}</p>
            
            <div className="row g-3 mb-4">
              <div className="col-md-3">
                <label className="form-label">Дата от </label>
                <input
                    type="datetime-local"
                    name="date_filter_from"
                    className="form-control form-control-sm"
                    value={tempDateValues.date_filter_from || dateFilters.date_filter_from}
                    onChange={handleDateChange}
                    onBlur={handleDateBlur}
                    step="1"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Дата до (с секундами)</label>
                <input
                    type="datetime-local"
                    name="date_filter_to"
                    className="form-control form-control-sm"
                    value={tempDateValues.date_filter_to || dateFilters.date_filter_to}
                    onChange={handleDateChange}
                    onBlur={handleDateBlur}
                    step="1"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Фильтр по месяцу</label>
                <select
                  className="form-control form-control-sm"
                  value={selectedMonthYear}
                  onChange={(e) => setSelectedMonthYear(e.target.value)}
                >
                  <option value="">Все месяцы</option>
                  {monthYearOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Блоки статистики */}
            <div style={{display: 'flex', gap: '20px', marginBottom: '20px'}}>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                minWidth: '200px',
                textAlign: 'center'
              }}>
                <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Всего обращений</h4>
                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#007bff'}}>
                  {ticketsCount.reduce((sum, item) => sum + item.count, 0)}
                </div>
                {missingDates.length > 0 && (
                  <div 
                    style={{fontSize: '12px', color: '#dc3545', marginTop: '5px', cursor: 'help'}}
                    title={`Нет данных за: ${missingDates.join(', ')}`}
                  >
                    Заполнены не все дни периода
                  </div>
                )}
              </div>

              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                minWidth: '200px',
                textAlign: 'center'
              }}>
                <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Проверено обращений</h4>
                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#28a745'}}>
                  {assessments.length}
                </div>
              </div>

              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                minWidth: '200px',
                textAlign: 'center'
              }}>
                <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>% проверенных обращений к общему количеству</h4>
                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#ffc107'}}>
                  {ticketsCount.reduce((sum, item) => sum + item.count, 0) > 0
                    ? ((assessments.length / ticketsCount.reduce((sum, item) => sum + item.count, 0)) * 100).toFixed(2) + '%'
                    : '0%'
                  }
                </div>
              </div>
              
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                minWidth: '200px',
                textAlign: 'center'
              }}>
                <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Результаты проверок</h4>
                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#28a745'}}>
                  {assessments.length > 0
                    ? (assessments.reduce((sum, a) => sum + (a.total_score || 0), 0) / assessments.length * 100).toFixed(2) + '%'
                    : '0%'
                  }
                </div>
              </div>
              
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                minWidth: '200px',
                textAlign: 'center'
              }}>
                <h4 style={{margin: '0 0 10px 0', color: '#6c757d'}}>Количество критов</h4>
                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#dc3545'}}>
                  {assessments.reduce((count, assessment) => {
                    return count + assessment.items.filter(item => item.critical && item.score === 0).length;
                  }, 0)}
                </div>
              </div>
            </div>

            <div className="tables-container">
              <div className="tables-row">
                <AssessmentTypeTable
                    assessments={assessments}
                    fieldOptions={fieldOptions?.assessment_type || []}
                />
                <ReceiptChannelTable
                    assessments={assessments}
                    fieldOptions={fieldOptions?.receipt_channel || []}
                />
                <ImpactTable
                    assessments={assessments}
                    fieldOptions={fieldOptions?.impact || []}
                />

              </div>

              <div className="tables-row">
                <OperatorsTable
                    assessments={assessments}
                    fieldOptions={{
                      impact: fieldOptions?.impact || [],
                      operators: fieldOptions?.operators || []
                    }}
                />
                <ProjectCategoryTable
                    assessments={assessments}
                    criteria={criteria}
                />
              </div>
              <div style={{width: '100%', marginBottom: '10px'}}>
                <label style={{marginRight: '10px'}}>Фильтр по impact: </label>
                <select
                    value={selectedImpact}
                    onChange={(e) => setSelectedImpact(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                >
                  <option value="">Все уровни impact</option>
                  {fieldOptions?.impact?.map(impact => (
                      <option key={impact.option_value} value={impact.option_value}>
                        {impact.option_text}
                      </option>
                  ))}
                </select>
              </div>
              <div className="tables-row">

                <OperatorCriteriaTable
                    assessments={selectedImpact ? assessments.filter(a => a.impact === selectedImpact) : assessments}
                    criteria={criteria}
                    fieldOptions={fieldOptions}
                />
              </div>

              <div className="tables-row">
                <OperatorServiceTable
                    assessments={assessments}
                    fieldOptions={fieldOptions}
                />
              </div>
              <div style={{width: '100%', marginBottom: '10px'}}>
                <label style={{marginRight: '10px'}}>Фильтр по impact: </label>
                <select
                    value={selectedImpact}
                    onChange={(e) => setSelectedImpact(e.target.value)}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc'
                    }}
                >
                  {fieldOptions?.impact?.filter(impact => impact.option_value !== '').map(impact => (
                      <option key={impact.option_value} value={impact.option_value}>
                        {impact.option_text}
                      </option>
                  ))}
                </select>
              </div>
              <div className="tables-row">
                <ImpactOperatorTable
                    assessments={selectedImpact ? assessments.filter(a => a.impact === selectedImpact) : assessments}
                    fieldOptions={fieldOptions?.operators || []}
                />
              </div>
            </div>
          </div>
      )}
    </div>
  );
};

export default AllAssessmentsPage;